// Configurações → Sistema → Integração → Balança (26/10/2026). Configura a
// balança plugada NESTE computador: cabo ou simulador, marca, porta e os
// parâmetros do cabo, com "Detectar automaticamente" e o peso ao vivo para
// testar. O modo (automático/manual), o peso mínimo e os módulos são da loja
// inteira e ficam em Preferências (BalancaPreferencias.jsx).
import { Cable, CheckCircle2, ChevronDown, Cpu, Scale, Search, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";

import { Switch } from "@/components/ui/switch";

import { webSerialDisponivel } from "./cabo";
import { lerConfig, paramsDaMarca, salvarConfig } from "./config";
import EscolherPortaDialog from "./EscolherPortaDialog";
import { MARCAS, MENSAGEM_ESTADO } from "./protocolo";
import { detectar, escolherPorta, escutar, MENSAGEM_CONEXAO } from "./servico";
import { aoMudarSimulador, colocarPeso, pesoSimulado } from "./simulador";
import { useConfigBalanca } from "./useBalanca";

const PARIDADES = { none: "sem paridade", even: "par", odd: "ímpar" };
const resumoCabo = (c) => `${c.baudRate} · ${c.dataBits} bits · ${PARIDADES[c.parity] || c.parity}`;
const kgTexto = (kg) => `${Number(kg || 0).toFixed(3).replace(".", ",")} kg`;

function Campo({ rotulo, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-muted-foreground">{rotulo}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const selectCls = "w-full h-10 px-3 rounded-lg border bg-background text-sm";

function PesoAoVivo() {
  const [leitura, setLeitura] = useState(null);
  const [firme, setFirme] = useState(null);
  useEffect(() => escutar(({ leitura: l, firmeKg }) => { setLeitura(l); setFirme(firmeKg); }), []);
  const ok = leitura?.estado === "ok";
  const msg = leitura ? MENSAGEM_CONEXAO[leitura.estado] || MENSAGEM_ESTADO[leitura.estado] || "" : "Lendo a balança…";
  return (
    <div className="rounded-xl border bg-muted/30 p-4 text-center" data-e2e="balanca-ao-vivo">
      <p className="text-4xl font-black tabular-nums" data-e2e="balanca-ao-vivo-peso">{ok ? kgTexto(leitura.kg) : leitura?.estado === "vazio" ? kgTexto(0) : "—"}</p>
      <p className={`text-sm mt-1 ${ok ? (firme != null ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600") : "text-muted-foreground"}`}>
        {ok ? (firme != null ? "Peso firme — é este que entra no pedido" : "Peso mexendo…") : msg}
      </p>
    </div>
  );
}

export default function IntegracaoBalanca() {
  const cfg = useConfigBalanca();
  const [testando, setTestando] = useState(false);
  const [avancado, setAvancado] = useState(false);
  const [deteccao, setDeteccao] = useState(null); // {passo, total, texto} | {achou} | {falhou}
  const [erroPorta, setErroPorta] = useState("");
  const [simKg, setSimKg] = useState(pesoSimulado());
  useEffect(() => aoMudarSimulador(setSimKg), []);
  const mudar = (parcial) => salvarConfig({ ...lerConfig(), ...parcial });
  const serial = webSerialDisponivel();

  const escolher = async () => {
    setErroPorta("");
    try {
      await escolherPorta();
    } catch (e) {
      if (e?.name === "NotFoundError") return; // fechou a janela sem escolher
      setErroPorta(e?.codigo ? MENSAGEM_CONEXAO[e.codigo] : "Não foi possível abrir a lista de portas. Se estiver no Núcleo Desktop, atualize o aplicativo.");
    }
  };

  const rodarDeteccao = async () => {
    setTestando(false);
    setDeteccao({ passo: 0, total: 0, texto: "Começando…" });
    try {
      const r = await detectar((passo, total, c) => setDeteccao({ passo, total, texto: resumoCabo(c) }));
      if (r) {
        mudar({ ...r.combinacao, pedirPeso: true });
        setDeteccao({ achou: `${resumoCabo(r.combinacao)} — ${r.leitura.estado === "ok" ? kgTexto(r.leitura.kg) : MENSAGEM_ESTADO[r.leitura.estado] || "respondeu"}` });
      } else {
        setDeteccao({ falhou: true });
      }
    } catch (e) {
      setDeteccao({ falhou: true, texto: MENSAGEM_CONEXAO[e?.codigo] });
    }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-e2e="integracao-balanca">
      <EscolherPortaDialog />
      <p className="text-sm text-muted-foreground">
        Para restaurante por quilo, açougue e hortifrúti: o produto marcado <b>Pesar na balança</b> pega o peso direto da
        balança ligada neste computador, no Balcão, nas Comandas, em Mesas e no Delivery. Esta configuração vale só para
        este computador. Automático ou manual, peso mínimo e módulos ficam em <b>Preferências</b>.
      </p>

      <div className="rounded-xl border p-4 flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-sm">Usar balança neste computador</p>
          <p className="text-xs text-muted-foreground">{cfg.ativa ? "Ligada" : "Desligada — os produtos por peso continuam com o peso digitado"}</p>
        </div>
        <Switch checked={cfg.ativa} onCheckedChange={(v) => mudar({ ativa: v })} data-e2e="balanca-ativa" />
      </div>

      {cfg.ativa && (<>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            { id: "cabo", Icon: Cable, titulo: "Balança no cabo", desc: "Serial ou USB ligada neste computador" },
            { id: "simulador", Icon: Cpu, titulo: "Simulador", desc: "Sem balança: para treinar e demonstrar" },
          ].map(({ id, Icon, titulo, desc }) => (
            <button key={id} type="button" onClick={() => mudar({ conexao: id })} data-e2e={`balanca-conexao-${id}`}
              className={`flex items-start gap-3 p-3 rounded-xl border text-left transition ${cfg.conexao === id ? "border-orange-500 bg-orange-500/5" : "hover:bg-muted/40"}`}>
              <Icon className={`w-5 h-5 mt-0.5 ${cfg.conexao === id ? "text-orange-500" : "text-muted-foreground"}`} />
              <span><span className="block text-sm font-semibold">{titulo}</span><span className="block text-xs text-muted-foreground">{desc}</span></span>
            </button>
          ))}
        </div>

        {cfg.conexao === "simulador" ? (
          <div className="rounded-xl border p-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              O simulador responde como uma balança: depois de mudar o peso, ele "mexe" por um instante e então firma.
              Enquanto ele estiver ligado, um controle flutuante aparece no canto da tela para mudar o peso em qualquer módulo.
            </p>
            <Campo rotulo={`Peso no simulador: ${kgTexto(simKg)}`}>
              <input type="range" min="0" max="3" step="0.005" value={simKg} data-e2e="balanca-sim-regua"
                onChange={(e) => { const v = Number(e.target.value); setSimKg(v); colocarPeso(v); }} className="w-full accent-orange-500" />
            </Campo>
          </div>
        ) : (
          <div className="rounded-xl border p-4 space-y-4">
            {!serial && (
              <div className="flex gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-sm text-amber-700 dark:text-amber-300">
                <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{MENSAGEM_CONEXAO.sem_serial}</span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Campo rotulo="Marca da balança">
                <select className={selectCls} value={cfg.marca} data-e2e="balanca-marca"
                  onChange={(e) => mudar({ marca: e.target.value, ...paramsDaMarca(e.target.value) })}>
                  {MARCAS.map((m) => <option key={m.id} value={m.id}>{m.nome}{m.modelos ? ` (${m.modelos})` : ""}</option>)}
                </select>
              </Campo>
              <Campo rotulo="Porta">
                <button type="button" onClick={escolher} disabled={!serial} data-e2e="balanca-escolher-porta"
                  className="w-full h-10 px-3 rounded-lg border text-sm text-left flex items-center justify-between gap-2 hover:bg-muted/40 disabled:opacity-50">
                  <span className="truncate">{cfg.porta?.nome || "Escolher a porta da balança"}</span>
                  <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              </Campo>
            </div>
            {erroPorta && <p className="text-xs text-red-500">{erroPorta}</p>}

            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={rodarDeteccao} disabled={!serial || !cfg.porta || (deteccao && deteccao.total && !deteccao.achou && !deteccao.falhou)}
                data-e2e="balanca-detectar"
                className="h-10 px-4 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold disabled:opacity-50">
                Detectar automaticamente
              </button>
              <span className="text-xs text-muted-foreground">Cabo: {resumoCabo(cfg)}{cfg.pedirPeso ? " · pede o peso" : " · contínua"}</span>
            </div>
            {deteccao && (
              <p className={`text-sm ${deteccao.achou ? "text-emerald-600 dark:text-emerald-400" : deteccao.falhou ? "text-red-500" : "text-muted-foreground"}`} data-e2e="balanca-deteccao">
                {deteccao.achou ? <><CheckCircle2 className="w-4 h-4 inline mr-1" />Achei a balança: {deteccao.achou}</>
                  : deteccao.falhou ? (deteccao.texto || "A balança não respondeu em nenhuma combinação. Confira se ela está ligada, o cabo e se a saída serial está habilitada no menu da balança.")
                  : `Testando ${deteccao.passo} de ${deteccao.total}: ${deteccao.texto}`}
              </p>
            )}

            <button type="button" onClick={() => setAvancado((v) => !v)} className="text-xs font-semibold text-muted-foreground inline-flex items-center gap-1">
              <ChevronDown className={`w-3.5 h-3.5 transition ${avancado ? "rotate-180" : ""}`} /> Ajuste manual do cabo
            </button>
            {avancado && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Campo rotulo="Velocidade">
                  <select className={selectCls} value={cfg.baudRate} onChange={(e) => mudar({ baudRate: Number(e.target.value) })}>
                    {[1200, 2400, 4800, 9600, 19200, 38400].map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Campo>
                <Campo rotulo="Bits de dados">
                  <select className={selectCls} value={cfg.dataBits} onChange={(e) => mudar({ dataBits: Number(e.target.value) })}>
                    {[7, 8].map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Campo>
                <Campo rotulo="Paridade">
                  <select className={selectCls} value={cfg.parity} onChange={(e) => mudar({ parity: e.target.value })}>
                    {Object.entries(PARIDADES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </Campo>
                <Campo rotulo="Bits de parada">
                  <select className={selectCls} value={cfg.stopBits} onChange={(e) => mudar({ stopBits: Number(e.target.value) })}>
                    {[1, 2].map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Campo>
                <Campo rotulo="Peso sem vírgula vem em">
                  <select className={selectCls} value={cfg.casas} onChange={(e) => mudar({ casas: Number(e.target.value) })}>
                    <option value={3}>gramas (00446 = 0,446 kg)</option>
                    <option value={2}>dezenas de grama (01250 = 12,50 kg)</option>
                  </select>
                </Campo>
                <Campo rotulo="Como a balança manda">
                  <select className={selectCls} value={cfg.pedirPeso ? "pedido" : "continua"} onChange={(e) => mudar({ pedirPeso: e.target.value === "pedido" })}>
                    <option value="pedido">Quando o sistema pede</option>
                    <option value="continua">Sem parar (contínua)</option>
                  </select>
                </Campo>
              </div>
            )}
          </div>
        )}

        <div className="rounded-xl border p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Scale className="w-4 h-4 text-muted-foreground" />
              <p className="font-semibold text-sm">Testar a balança</p>
            </div>
            <button type="button" onClick={() => setTestando((v) => !v)} data-e2e="balanca-testar"
              className="h-9 px-3 rounded-lg border text-sm font-semibold hover:bg-muted/40">
              {testando ? "Parar" : "Ler o peso ao vivo"}
            </button>
          </div>
          {testando && <PesoAoVivo />}
        </div>
      </>)}
    </div>
  );
}
